import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBox, faCheck, faTruck, faSpinner, faClipboardCheck,
  faBoxOpen, faCheckCircle, faWarehouse, faUser, faStore
} from '@fortawesome/free-solid-svg-icons';
import { trackingAPI } from '../services/api';
import './OrderTracking.css';

const OrderTracking = ({ orderId: propOrderId, onClose }) => {
  const { orderId: paramOrderId } = useParams();
  const navigate = useNavigate();
  const orderId = propOrderId || paramOrderId;
  const handleClose = onClose || (() => navigate(-1));

  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTracking = async () => {
      try {
        setLoading(true);
        const response = await trackingAPI.getOrderTracking(orderId);
        if (response && response.data) {
          setTracking(response.data);
        }
      } catch (err) {
        setError(err.message || 'Failed to load tracking information');
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchTracking();
    }
  }, [orderId]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return faBox;
      case 'confirmed':
        return faClipboardCheck;
      case 'processing':
        return faBoxOpen;
      case 'shipped':
        return faTruck;
      case 'delivered':
        return faCheckCircle;
      default:
        return faBox;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="AutoboyExpress-tracking-modal">
        <div className="AutoboyExpress-tracking-content">
          <div className="AutoboyExpress-tracking-loading">
            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            <p>Loading tracking information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="AutoboyExpress-tracking-modal">
        <div className="AutoboyExpress-tracking-content">
          <div className="AutoboyExpress-tracking-error">
            <p>{error}</p>
            <button onClick={handleClose} className="AutoboyExpress-tracking-close-btn">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!tracking) {
    return null;
  }

  const { order, timeline, events } = tracking;

  return (
    <div className="AutoboyExpress-tracking-modal" onClick={handleClose}>
      <div className="AutoboyExpress-tracking-content" onClick={(e) => e.stopPropagation()}>
        <div className="AutoboyExpress-tracking-header">
          <div>
            <h2>Order Tracking</h2>
            <p className="AutoboyExpress-tracking-order-number">Order #{order?.order_number}</p>
          </div>
          <button className="AutoboyExpress-tracking-close-icon" onClick={handleClose}>
            ×
          </button>
        </div>

        {order?.tracking_number && (
          <div className="AutoboyExpress-tracking-info">
            <div className="AutoboyExpress-tracking-info-item">
              <strong>Tracking Number:</strong>
              <span>{order.tracking_number}</span>
            </div>
            {order?.carrier_name && (
              <div className="AutoboyExpress-tracking-info-item">
                <strong>Carrier:</strong>
                <span>{order.carrier_name}</span>
              </div>
            )}
          </div>
        )}

        {/* FabsTech 3-Stop Journey — per API doc mandatory warehouse flow */}
        {(() => {
          const status = order?.status || 'pending';

          // Map AutoboyExpress status → active stop index (0-based, -1 = not started)
          // Doc: pending→confirmed→processing→shipped→delivered
          // Stop 0: Seller Pickup | Stop 1: Warehouse | Stop 2: Buyer Delivery
          const stopIndex =
            status === 'delivered'  ? 3 :
            status === 'shipped'    ? 2 :
            status === 'processing' ? 1 :
            status === 'confirmed'  ? 0 : 0;

          const stops = [
            {
              icon: faStore,
              label: 'Seller Pickup',
              sub: 'Rider collects item from seller',
              color: '#f59e0b',
            },
            {
              icon: faWarehouse,
              label: 'AutoboyExpress Warehouse',
              sub: 'Inspection & quality check',
              color: '#3b82f6',
            },
            {
              icon: faUser,
              label: 'Buyer Delivery',
              sub: 'Inspected item delivered to you',
              color: '#10b981',
            },
          ];

          return (
            <div style={{ margin: '16px 0', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <p style={{ margin: '0 0 14px', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>
                FabsTech 3-Stop Delivery
              </p>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
                {stops.map((stop, i) => {
                  const done   = i < stopIndex;
                  const active = i === stopIndex - 1 || (stopIndex === 0 && i === 0 && ['confirmed','processing'].includes(status));
                  return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                      {/* Connector line */}
                      {i < stops.length - 1 && (
                        <div style={{
                          position: 'absolute', top: '19px', left: '50%', right: '-50%',
                          height: '3px', zIndex: 0,
                          background: done ? stop.color : '#e2e8f0',
                          transition: 'background 0.4s',
                        }} />
                      )}
                      {/* Icon circle */}
                      <div style={{
                        width: '40px', height: '40px', borderRadius: '50%', zIndex: 1,
                        background: done ? stop.color : active ? stop.color + '33' : '#e2e8f0',
                        border: `3px solid ${done || active ? stop.color : '#cbd5e1'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: done ? '#fff' : active ? stop.color : '#94a3b8',
                        fontSize: '15px',
                        boxShadow: active ? `0 0 0 4px ${stop.color}22` : 'none',
                        transition: 'all 0.3s',
                      }}>
                        {done
                          ? <FontAwesomeIcon icon={faCheck} />
                          : <FontAwesomeIcon icon={stop.icon} />}
                      </div>
                      {/* Label */}
                      <p style={{ margin: '8px 0 2px', fontSize: '12px', fontWeight: 700, textAlign: 'center', color: done || active ? '#1e293b' : '#94a3b8' }}>
                        {stop.label}
                      </p>
                      <p style={{ margin: 0, fontSize: '11px', textAlign: 'center', color: '#94a3b8', padding: '0 4px' }}>
                        {stop.sub}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        <div className="AutoboyExpress-tracking-timeline">
          {timeline && timeline.map((item, index) => (
            <div
              key={index}
              className={`AutoboyExpress-tracking-step ${item.completed ? 'completed' : 'pending'}`}
            >
              <div className="AutoboyExpress-tracking-step-icon">
                {item.completed ? (
                  <FontAwesomeIcon icon={faCheck} />
                ) : (
                  <FontAwesomeIcon icon={getStatusIcon(item.status)} />
                )}
              </div>
              <div className="AutoboyExpress-tracking-step-content">
                <h4>{item.title}</h4>
                <p>{item.description}</p>
                {item.timestamp && (
                  <span className="AutoboyExpress-tracking-step-time">
                    {formatDate(item.timestamp)}
                  </span>
                )}
                {item.tracking_number && (
                  <div className="AutoboyExpress-tracking-step-meta">
                    Tracking: {item.tracking_number}
                    {item.carrier && ` (${item.carrier})`}
                  </div>
                )}
              </div>
              {index < timeline.length - 1 && (
                <div className={`AutoboyExpress-tracking-step-line ${item.completed ? 'completed' : ''}`} />
              )}
            </div>
          ))}
        </div>

        {events && events.length > 0 && (
          <div className="AutoboyExpress-tracking-events">
            <h3>Detailed Tracking Events</h3>
            <div className="AutoboyExpress-tracking-events-list">
              {events.map((event, index) => (
                <div key={index} className="AutoboyExpress-tracking-event">
                  <div className="AutoboyExpress-tracking-event-time">
                    {formatDate(event.timestamp)}
                  </div>
                  <div className="AutoboyExpress-tracking-event-details">
                    <strong>{event.description}</strong>
                    {event.location && <p className="AutoboyExpress-tracking-event-location">{event.location}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="AutoboyExpress-tracking-footer">
          <button onClick={handleClose} className="AutoboyExpress-tracking-close-btn">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
